/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import {
    Button, Input, Modal, message, Tag, Select, Space,
    Tooltip, Row, Col, DatePicker, Alert
} from 'antd';
import {
    PlusOutlined, SearchOutlined, ToolOutlined,
    FileTextOutlined, WarningOutlined, DashboardOutlined
} from '@ant-design/icons';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList
} from 'recharts';
import moment from 'moment';
import FleetService from '../../services/FleetService';

const { TextArea } = Input;
const { Option }   = Select;

// ─── Constants ───────────────────────────────────────────────────────────────
// ─── SHARED: Period Filter Component ────────────────────────────────────────
// Drop this into any file that needs time period filtering
// Usage: <PeriodFilter value={period} onChange={setPeriod} />
// period = { preset: 'this_month'|'last_3m'|'this_year'|'last_year'|'custom'|'all', from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
// Filter data: filterByPeriod(data, period, dateField)

const { RangePicker } = DatePicker;

const PRESETS = [
    { key: 'this_month', label: 'This Month',   from: () => moment().startOf('month'),   to: () => moment() },
    { key: 'last_3m',    label: 'Last 3 Months',from: () => moment().subtract(3,'months'),to:() => moment() },
    { key: 'this_year',  label: 'This Year',     from: () => moment().startOf('year'),    to: () => moment() },
    { key: 'last_year',  label: 'Last Year',     from: () => moment().subtract(1,'year').startOf('year'), to: () => moment().subtract(1,'year').endOf('year') },
    { key: 'all',        label: 'All Time',      from: () => null, to: () => null },
];

const DEFAULT_PERIOD = { preset: 'this_year', from: moment().startOf('year').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') };

function PeriodFilter({ value, onChange, style }) {
    const cur = value || DEFAULT_PERIOD;
    const handlePreset = (preset) => {
        const p = PRESETS.find(x => x.key === preset);
        onChange({
            preset,
            from: p.from() ? p.from().format('YYYY-MM-DD') : '',
            to:   p.to()   ? p.to().format('YYYY-MM-DD')   : '',
        });
    };
    const handleRange = ([s, e]) => {
        onChange({
            preset: 'custom',
            from: s ? s.format('YYYY-MM-DD') : '',
            to:   e ? e.format('YYYY-MM-DD') : '',
        });
    };
    return (
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', ...style }}>
            <span style={{ fontSize:11, color:'#888', whiteSpace:'nowrap' }}>Period:</span>
            {PRESETS.map(p => (
                <button key={p.key} onClick={() => handlePreset(p.key)} style={{
                    padding:'3px 10px', border:'1px solid', borderRadius:12, cursor:'pointer',
                    fontSize:11, fontWeight: cur.preset===p.key ? 600 : 400,
                    background: cur.preset===p.key ? '#1a3a5c' : '#fff',
                    borderColor: cur.preset===p.key ? '#1a3a5c' : '#d9d9d9',
                    color: cur.preset===p.key ? '#fff' : '#595959',
                }}>{p.label}</button>
            ))}
            <RangePicker size="small" style={{ width:210 }}
                value={cur.preset==='custom' && cur.from && cur.to
                    ? [moment(cur.from), moment(cur.to)] : null}
                onChange={handleRange}
                format="DD MMM YYYY"
                placeholder={['Custom from','Custom to']}
            />
            {cur.from && cur.to && (
                <span style={{ fontSize:10, color:'#1a5b9a', background:'#e6f4ff',
                    padding:'2px 8px', borderRadius:8, whiteSpace:'nowrap' }}>
                    {moment(cur.from).format('DD MMM YYYY')} → {moment(cur.to).format('DD MMM YYYY')}
                </span>
            )}
        </div>
    );
}

function filterByPeriod(data, period, dateField) {
    if (!period || period.preset === 'all' || (!period.from && !period.to)) return data;
    return data.filter(r => {
        const d = r[dateField];
        if (!d || d === '0000-00-00') return false;
        const m = moment(d);
        if (period.from && m.isBefore(moment(period.from), 'day')) return false;
        if (period.to   && m.isAfter(moment(period.to),   'day')) return false;
        return true;
    });
}

const COST_TYPES = [
    'Scheduled Service','Unscheduled Repair','Tyre Replacement',
    'Battery Replacement','Body Repair','Electrical Repair',
    'Fuel System','Brake Service','Suspension & Steering',
    'Air Conditioning','Windscreen','Insurance','Road Licence',
    'Fitness Certificate','Other',
];
const SERVICE_TYPES = [
    '10,000 km Service','20,000 km Service','30,000 km Service',
    '40,000 km Service','Major Service','Minor Service',
    'Oil & Filter Change','Full Inspection','Pre-Trip Inspection','Other',
];

// Next service interval by fuel type (km)
const SERVICE_INTERVAL = (fuelType) => {
    if (!fuelType) return 10000;
    const f = fuelType.toLowerCase();
    if (f === 'diesel') return 15000;
    if (f === 'electric') return 30000; // EV check every 30k
    return 10000; // petrol / hybrid / default
};

const fmt  = (v) => v || '—';
const fmtD = (v) => (!v || v === '0000-00-00') ? '—' : moment(v).format('DD MMM YYYY');
const fmtE = (v) => v ? `E ${Number(v).toLocaleString()}` : '—';

// ─── Service Due Banner component ─────────────────────────────────────────────
function ServiceDueBadge({ flag }) {
    if (!flag) return null;
    if (flag === 'overdue') return (
        <span style={{
            fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
            background: '#fff1f0', border: '1px solid #ffa39e', color: '#cf1322',
            whiteSpace: 'nowrap',
        }}>🔧 SERVICE OVERDUE</span>
    );
    if (flag === 'due_soon') return (
        <span style={{
            fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
            background: '#fffbe6', border: '1px solid #ffe58f', color: '#d48806',
            whiteSpace: 'nowrap',
        }}>🔧 SERVICE DUE &lt;500km</span>
    );
    return null;
}

// ─── Log Cost Form Modal ──────────────────────────────────────────────────────
function CostFormModal({ open, vehicles, onSaved, onClose }) {
    const EMPTY = {
        vehicle_id: null, cost_type: '', amount: '', cost_date: null,
        service_type: '', service_provider: '', parts_replaced: '',
        performed_by: '', approved_by: '', job_card_no: '',
        warranty_info: '', odometer_at_service: '', notes: '',
        next_service_km: '', date_sent_for_service: null, date_returned: null,
    };
    const [form, setForm]       = useState(EMPTY);
    const [saving, setSaving]   = useState(false);
    const [selectedVeh, setSelectedVeh] = useState(null);

    useEffect(() => { if (open) { setForm(EMPTY); setSelectedVeh(null); } }, [open]);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    // When vehicle selected — auto-fill next service km from fuel type
    const handleVehicleChange = (id) => {
        set('vehicle_id', id);
        const veh = vehicles.find(v => v.id === id);
        setSelectedVeh(veh || null);
        if (veh && veh.current_odometer) {
            const interval = SERVICE_INTERVAL(veh.fuel_type);
            set('next_service_km', Number(veh.current_odometer) + interval);
        }
    };

    // Auto-calc next service when odometer changes
    const handleOdometerChange = (val) => {
        set('odometer_at_service', val);
        if (selectedVeh && val) {
            const interval = SERVICE_INTERVAL(selectedVeh.fuel_type);
            set('next_service_km', Number(val) + interval);
        }
    };

    // Turnaround days
    const turnaround = useMemo(() => {
        if (form.date_sent_for_service && form.date_returned) {
            const d = moment(form.date_returned).diff(moment(form.date_sent_for_service), 'days');
            return d >= 0 ? d : null;
        }
        return null;
    }, [form.date_sent_for_service, form.date_returned]);

    const handleSave = async () => {
        if (!form.vehicle_id) { message.error('Select a vehicle');   return; }
        if (!form.cost_type)  { message.error('Select a cost type'); return; }
        if (!form.amount)     { message.error('Enter the amount');    return; }
        if (!form.cost_date)  { message.error('Enter the service date'); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                cost_date:             moment(form.cost_date).format('YYYY-MM-DD'),
                date_sent_for_service: form.date_sent_for_service
                    ? moment(form.date_sent_for_service).format('YYYY-MM-DD') : null,
                date_returned:         form.date_returned
                    ? moment(form.date_returned).format('YYYY-MM-DD') : null,
                next_service_km:       form.next_service_km || null,
            };
            const res = await FleetService.saveCost(payload);
            if (Number(res?.data?.code) === 200) {
                message.success('Maintenance record saved');
                onSaved();
            } else { message.error(res?.data?.message || 'Failed to save'); }
        } catch (e) { message.error(e.message || 'Error saving'); }
        finally { setSaving(false); }
    };

    const lbl  = { fontSize: 12, color: '#666', marginBottom: 4 };
    const fbox = { marginBottom: 14 };

    return (
        <Modal open={open} onCancel={onClose} width={760} footer={null} destroyOnClose
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <ToolOutlined style={{ color: '#d46b08' }} />
                    <span>Log Maintenance / Cost Record</span>
                </div>
            }
        >
            {/* Section 1 — Vehicle & Dates */}
            <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontWeight: 600, color: '#d46b08', marginBottom: 12 }}>🚗 Vehicle & Dates</div>
                <Row gutter={16}>
                    <Col span={16}>
                        <div style={fbox}>
                            <div style={lbl}>Vehicle <span style={{ color: 'red' }}>*</span></div>
                            <Select showSearch style={{ width: '100%' }} value={form.vehicle_id}
                                onChange={handleVehicleChange}
                                placeholder="Search by registration plate..."
                                optionFilterProp="children">
                                {vehicles.map(v => (
                                    <Option key={v.id} value={v.id}>
                                        {v.registration_no} — {v.vehicle_type}
                                        {v.fuel_type ? ` (${v.fuel_type})` : ''}
                                        {v.current_odometer ? ` | ${Number(v.current_odometer).toLocaleString()} km` : ''}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Service Date <span style={{ color: 'red' }}>*</span></div>
                            <DatePicker style={{ width: '100%' }}
                                value={form.cost_date ? moment(form.cost_date) : null}
                                onChange={d => set('cost_date', d ? d.format('YYYY-MM-DD') : null)}
                                format="DD MMM YYYY"
                                disabledDate={d => d && d.isAfter(moment())} />
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Date Sent for Service</div>
                            <DatePicker style={{ width: '100%' }}
                                value={form.date_sent_for_service ? moment(form.date_sent_for_service) : null}
                                onChange={d => set('date_sent_for_service', d ? d.format('YYYY-MM-DD') : null)}
                                format="DD MMM YYYY" />
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Date Returned</div>
                            <DatePicker style={{ width: '100%' }}
                                value={form.date_returned ? moment(form.date_returned) : null}
                                onChange={d => set('date_returned', d ? d.format('YYYY-MM-DD') : null)}
                                format="DD MMM YYYY" />
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Turnaround Time</div>
                            <div style={{
                                height: 32, display: 'flex', alignItems: 'center',
                                fontWeight: 700, fontSize: 14,
                                color: turnaround !== null
                                    ? (turnaround > 14 ? '#cf1322' : turnaround > 7 ? '#d46b08' : '#389e0d')
                                    : '#bbb',
                            }}>
                                {turnaround !== null ? `${turnaround} day${turnaround !== 1 ? 's' : ''}` : '—'}
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Section 2 — Service Details */}
            <div style={{ background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontWeight: 600, color: '#2f54eb', marginBottom: 12 }}>🔧 Service Details</div>
                <Row gutter={16}>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Cost / Work Type <span style={{ color: 'red' }}>*</span></div>
                            <Select style={{ width: '100%' }} value={form.cost_type}
                                onChange={v => set('cost_type', v)} placeholder="Select type">
                                {COST_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                            </Select>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Service Type</div>
                            <Select style={{ width: '100%' }} value={form.service_type}
                                onChange={v => set('service_type', v)} placeholder="Select service" allowClear>
                                {SERVICE_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                            </Select>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Odometer at Service (km)</div>
                            <Input type="number" value={form.odometer_at_service}
                                onChange={e => handleOdometerChange(e.target.value)}
                                placeholder="km reading" />
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>
                                Next Service Due (km)
                                {selectedVeh?.fuel_type && (
                                    <span style={{ color: '#1677ff', fontSize: 10, marginLeft: 6 }}>
                                        auto: {selectedVeh.fuel_type === 'Diesel' ? '+15,000' : '+10,000'} km
                                    </span>
                                )}
                            </div>
                            <Input type="number" value={form.next_service_km}
                                onChange={e => set('next_service_km', e.target.value)}
                                placeholder="Auto-calculated" />
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Job Card / Invoice No.</div>
                            <Input value={form.job_card_no}
                                onChange={e => set('job_card_no', e.target.value)}
                                placeholder="e.g. JC-2026-001" />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Service Provider / Garage</div>
                            <Input value={form.service_provider}
                                onChange={e => set('service_provider', e.target.value)}
                                placeholder="e.g. Toyota Swaziland, Manzini Garage" />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Warranty / Guarantee Info</div>
                            <Input value={form.warranty_info}
                                onChange={e => set('warranty_info', e.target.value)}
                                placeholder="e.g. 3 months / 5,000 km" />
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={fbox}>
                            <div style={lbl}>Parts Replaced</div>
                            <Input value={form.parts_replaced}
                                onChange={e => set('parts_replaced', e.target.value)}
                                placeholder="e.g. Oil filter, Air filter, Brake pads (front)" />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Performed By</div>
                            <Input value={form.performed_by}
                                onChange={e => set('performed_by', e.target.value)}
                                placeholder="Technician name" />
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Section 3 — Cost & Notes */}
            <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '14px 16px', marginBottom: 20 }}>
                <div style={{ fontWeight: 600, color: '#389e0d', marginBottom: 12 }}>💰 Cost & Notes</div>
                <Row gutter={16}>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Amount (E) <span style={{ color: 'red' }}>*</span></div>
                            <Input type="number" value={form.amount}
                                onChange={e => set('amount', e.target.value)}
                                placeholder="0.00" />
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Approved By</div>
                            <Input value={form.approved_by}
                                onChange={e => set('approved_by', e.target.value)}
                                placeholder="Approving officer name" />
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={fbox}>
                            <div style={lbl}>Notes</div>
                            <TextArea rows={2} value={form.notes}
                                onChange={e => set('notes', e.target.value)}
                                placeholder="Additional notes..." />
                        </div>
                    </Col>
                </Row>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button type="primary" loading={saving} onClick={handleSave}
                    style={{ background: '#d46b08', borderColor: '#d46b08' }}>
                    Save Record
                </Button>
            </div>
        </Modal>
    );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function CostDetail({ record, vehicle, onClose }) {
    if (!record) return null;
    const row = (label, value) => (
        <div style={{ display: 'flex', padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
            <span style={{ width: 190, fontSize: 12, color: '#888', flexShrink: 0 }}>{label}</span>
            <span style={{ fontSize: 12, fontWeight: 500 }}>{value || '—'}</span>
        </div>
    );

    const turnaround = (record.date_sent_for_service && record.date_returned
        && record.date_sent_for_service !== '0000-00-00'
        && record.date_returned !== '0000-00-00')
        ? moment(record.date_returned).diff(moment(record.date_sent_for_service), 'days')
        : null;

    return (
        <Modal open={!!record} onCancel={onClose} width={560} destroyOnClose
            footer={<Button onClick={onClose}>Close</Button>}
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FileTextOutlined />
                    <span>Maintenance Record — {vehicle}</span>
                </div>
            }
        >
            {row('Service Date',      fmtD(record.cost_date))}
            {row('Date Sent',         fmtD(record.date_sent_for_service))}
            {row('Date Returned',     fmtD(record.date_returned))}
            {turnaround !== null && row('Turnaround Time',
                <span style={{ fontWeight: 700, color: turnaround > 14 ? '#cf1322' : turnaround > 7 ? '#d46b08' : '#389e0d' }}>
                    {turnaround} day{turnaround !== 1 ? 's' : ''}
                </span>
            )}
            {row('Cost / Work Type',  record.cost_type)}
            {row('Service Type',      record.service_type)}
            {row('Amount',            fmtE(record.amount))}
            {row('Odometer at Service', record.odometer_at_service
                ? `${Number(record.odometer_at_service).toLocaleString()} km` : null)}
            {row('Next Service Due',  record.next_service_km
                ? `${Number(record.next_service_km).toLocaleString()} km` : null)}
            {row('Service Provider',  record.service_provider)}
            {row('Job Card / Invoice',record.job_card_no)}
            {row('Parts Replaced',    record.parts_replaced)}
            {row('Performed By',      record.performed_by)}
            {row('Approved By',       record.approved_by_name || record.approved_by)}
            {row('Warranty Info',     record.warranty_info)}
            {row('Recorded By',       record.recorded_by_name)}
            {record.notes && row('Notes', record.notes)}
        </Modal>
    );
}


// ─── Anomaly detector ─────────────────────────────────────────────────────────
function detectMaintenanceAnomalies(costs, vehicles) {
    const issues = [];
    const vehMap = {};
    vehicles.forEach(v => { vehMap[String(v.id)] = v; });

    // Vehicles overdue for service
    vehicles.filter(v => v.service_due_flag === 'overdue').forEach(v => {
        issues.push({ type:'error', msg:`${v.registration_no} — SERVICE OVERDUE (flag set)` });
    });

    // Very high single cost > E 20,000
    costs.forEach(c => {
        if (Number(c.amount) > 20000) {
            const reg = vehMap[String(c.vehicle_id)]?.reg || `Vehicle #${c.vehicle_id}`;
            issues.push({ type:'warn', msg:`${reg} — High cost record: E ${Number(c.amount).toLocaleString()} (${c.cost_type})` });
        }
    });

    // Same vehicle > 3 repairs in 3 months
    const recentCutoff = moment().subtract(3,'months');
    const recentByVeh = {};
    costs.filter(c => moment(c.cost_date).isAfter(recentCutoff))
        .forEach(c => { recentByVeh[c.vehicle_id] = (recentByVeh[c.vehicle_id]||0)+1; });
    Object.entries(recentByVeh).forEach(([vid, count]) => {
        if (count > 3) {
            const reg = vehMap[String(vid)]?.reg || `Vehicle #${vid}`;
            issues.push({ type:'warn', msg:`${reg} — ${count} maintenance records in last 90 days (possible chronic issue)` });
        }
    });

    // Turnaround > 21 days
    costs.filter(c => c.date_sent_for_service && c.date_returned
        && c.date_sent_for_service !== '0000-00-00' && c.date_returned !== '0000-00-00')
        .forEach(c => {
            const days = moment(c.date_returned).diff(moment(c.date_sent_for_service),'days');
            if (days > 21) {
                const reg = vehMap[String(c.vehicle_id)]?.reg || `Vehicle #${c.vehicle_id}`;
                issues.push({ type:'warn', msg:`${reg} — Long turnaround: ${days} days out of service (${c.cost_type})` });
            }
        });

    return issues;
}

// ─── Mini Dashboard ───────────────────────────────────────────────────────────
function MaintenanceDashboard({ costs, vehicles, period, onPeriodChange }) {
    const toN = (v) => Number(v || 0);

    const byType = useMemo(() => {
        const m = {};
        costs.forEach(c => { m[c.cost_type||'Other'] = (m[c.cost_type||'Other']||0) + toN(c.amount); });
        return Object.entries(m).map(([name,value]) => ({name,value}))
            .sort((a,b)=>b.value-a.value).slice(0,7);
    }, [costs]);

    const byMonth = useMemo(() => {
        const m = {};
        costs.forEach(c => {
            const mo = moment(c.cost_date).format('MMM YY');
            m[mo] = (m[mo]||0) + toN(c.amount);
        });
        return Object.entries(m).slice(-8).map(([month,cost]) => ({month,cost}));
    }, [costs]);

    const topCostVehicles = useMemo(() => {
        const m = {};
        costs.forEach(c => { m[c.vehicle_id] = (m[c.vehicle_id]||0) + toN(c.amount); });
        const vMap = {};
        vehicles.forEach(v => { vMap[v.id] = v.registration_no; });
        return Object.entries(m).map(([id,cost]) => ({ name: vMap[id]||`#${id}`, cost }))
            .sort((a,b)=>b.cost-a.cost).slice(0,6);
    }, [costs, vehicles]);

    const turnaroundDist = useMemo(() => {
        const ranges = { '1-3d':0, '4-7d':0, '8-14d':0, '15-21d':0, '>21d':0 };
        costs.filter(c => c.date_sent_for_service && c.date_returned
            && c.date_sent_for_service !== '0000-00-00' && c.date_returned !== '0000-00-00')
            .forEach(c => {
                const d = moment(c.date_returned).diff(moment(c.date_sent_for_service),'days');
                if (d <= 3) ranges['1-3d']++;
                else if (d <= 7) ranges['4-7d']++;
                else if (d <= 14) ranges['8-14d']++;
                else if (d <= 21) ranges['15-21d']++;
                else ranges['>21d']++;
            });
        return Object.entries(ranges).map(([range,count]) => ({range,count}));
    }, [costs]);

    const anomalies = useMemo(() => detectMaintenanceAnomalies(costs, vehicles), [costs, vehicles]);

    return (
        <div style={{ background:'#fafafa', border:'1px solid #e8e8e8', borderRadius:8, padding:'14px 16px', marginBottom:20 }}>
            {/* Period filter */}
            <PeriodFilter value={period} onChange={onPeriodChange} style={{ marginBottom:12 }} />
            {costs.length === 0 && (
                <div style={{ textAlign:'center', padding:16, color:'#aaa', fontSize:12 }}>
                    No maintenance records in selected period
                </div>
            )}
            {/* Anomaly banner */}
            {anomalies.length > 0 && (
                <div style={{ background:'#fff1f0', border:'1px solid #ffa39e', borderRadius:6,
                    padding:'8px 14px', marginBottom:14 }}>
                    <div style={{ fontWeight:700, color:'#cf1322', fontSize:12, marginBottom:6 }}>
                        <WarningOutlined style={{ marginRight:6 }} />
                        {anomalies.length} irregularit{anomalies.length>1?'ies':'y'} detected
                    </div>
                    {anomalies.map((a,i) => (
                        <div key={i} style={{ fontSize:11, display:'flex', gap:6, marginBottom:3 }}>
                            <Tag color={a.type==='error'?'red':'orange'} style={{ fontSize:9 }}>
                                {a.type==='error'?'Error':'Warning'}
                            </Tag>
                            {a.msg}
                        </div>
                    ))}
                </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1.4fr 1fr 1fr', gap:12 }}>
                {/* Cost by work type */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>Cost by Work Type (E)</div>
                    <ResponsiveContainer width="100%" height={140}>
                        <BarChart data={byType} layout="vertical" margin={{ top:0, right:50, left:110, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:9 }} tickFormatter={v => `E${(v/1000).toFixed(0)}k`} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:9 }} width={105} />
                            <RTooltip formatter={v => [`E ${v.toLocaleString()}`, 'Cost']} />
                            <Bar dataKey="value" fill="#d46b08" radius={[0,3,3,0]}>
                                <LabelList dataKey="value" position="right" style={{ fontSize:9 }}
                                    formatter={v => `E${(v/1000).toFixed(0)}k`} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Monthly spend */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>Monthly Spend Trend (E)</div>
                    <ResponsiveContainer width="100%" height={140}>
                        <BarChart data={byMonth} margin={{ top:0, right:5, left:-10, bottom:0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" tick={{ fontSize:9 }} />
                            <YAxis tick={{ fontSize:9 }} tickFormatter={v => `E${(v/1000).toFixed(0)}k`} />
                            <RTooltip formatter={v => [`E ${v.toLocaleString()}`, 'Spend']} />
                            <Bar dataKey="cost" fill="#1a5b9a" radius={[3,3,0,0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Top vehicles by cost */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>Top 6 Vehicles (E)</div>
                    <ResponsiveContainer width="100%" height={140}>
                        <BarChart data={topCostVehicles} layout="vertical"
                            margin={{ top:0, right:40, left:55, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:9 }} tickFormatter={v => `E${(v/1000).toFixed(0)}k`} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={50} />
                            <RTooltip formatter={v => [`E ${v.toLocaleString()}`, 'Total Cost']} />
                            <Bar dataKey="cost" fill="#389e0d" radius={[0,3,3,0]}>
                                <LabelList dataKey="cost" position="right" style={{ fontSize:9 }}
                                    formatter={v => `E${(v/1000).toFixed(0)}k`} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Turnaround distribution */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>Turnaround Distribution</div>
                    <ResponsiveContainer width="100%" height={140}>
                        <BarChart data={turnaroundDist} margin={{ top:0, right:5, left:-15, bottom:0 }}>
                            <XAxis dataKey="range" tick={{ fontSize:9 }} />
                            <YAxis tick={{ fontSize:9 }} allowDecimals={false} />
                            <RTooltip />
                            <Bar dataKey="count" radius={[3,3,0,0]}>
                                {turnaroundDist.map((d,i) => (
                                    <Cell key={i} fill={d.range==='>21d'?'#cf1322':d.range==='15-21d'?'#d46b08':'#52c41a'} />
                                ))}
                                <LabelList dataKey="count" position="top" style={{ fontSize:9 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FleetMaintenance() {
    const [costs,      setCosts]      = useState([]);
    const [vehicles,   setVehicles]   = useState([]);
    const [loading,    setLoading]    = useState(false);
    const [showDash,   setShowDash]   = useState(true);
    const [period,     setPeriod]     = useState(DEFAULT_PERIOD);
    const [formOpen,   setFormOpen]   = useState(false);
    const [detailRec,  setDetailRec]  = useState(null);
    const [search,     setSearch]     = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterVeh,  setFilterVeh]  = useState('');
    const [showDueOnly,setShowDueOnly]= useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [cRes, vRes] = await Promise.all([
                FleetService.getCosts(''),
                FleetService.getVehiclesList(),
            ]);
            if (Number(cRes?.data?.code) === 200) setCosts(cRes.data.result || []);
            if (Number(vRes?.data?.code) === 200) setVehicles(vRes.data.result || []);
        } catch { message.error('Failed to load maintenance records'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    // Build vehicle lookup map
    const vehMap = useMemo(() => {
        const m = {};
        vehicles.forEach(v => {
            m[String(v.id)] = {
                reg: v.registration_no, type: v.vehicle_type,
                facility: v.facility, fuel_type: v.fuel_type,
                current_odometer: v.current_odometer,
                service_due_flag: v.service_due_flag,
            };
        });
        return m;
    }, [vehicles]);

    // Build latest cost per vehicle → for service-due tracking
    const latestCostMap = useMemo(() => {
        const m = {};
        [...costs].sort((a, b) => new Date(b.cost_date) - new Date(a.cost_date))
            .forEach(c => {
                if (!m[String(c.vehicle_id)]) m[String(c.vehicle_id)] = c;
            });
        return m;
    }, [costs]);

    // Vehicles due/overdue for service
    const serviceDueVehicles = useMemo(() => {
        return vehicles.filter(v => v.service_due_flag === 'overdue' || v.service_due_flag === 'due_soon')
            .map(v => ({
                ...v,
                latestCost: latestCostMap[String(v.id)],
                kmLeft: v.current_odometer && latestCostMap[String(v.id)]?.next_service_km
                    ? Number(latestCostMap[String(v.id)].next_service_km) - Number(v.current_odometer)
                    : null,
            }))
            .sort((a, b) => (a.kmLeft ?? Infinity) - (b.kmLeft ?? Infinity));
    }, [vehicles, latestCostMap]);

    // Stats
    const totalCost  = costs.reduce((s, c) => s + Number(c.amount || 0), 0);
    const monthCost  = costs
        .filter(c => moment(c.cost_date).isSame(moment(), 'month'))
        .reduce((s, c) => s + Number(c.amount || 0), 0);
    const uniqueVehs = new Set(costs.map(c => String(c.vehicle_id))).size;
    const avgTurnaround = useMemo(() => {
        const withDates = costs.filter(c =>
            c.date_sent_for_service && c.date_returned
            && c.date_sent_for_service !== '0000-00-00'
            && c.date_returned !== '0000-00-00'
        );
        if (!withDates.length) return null;
        const total = withDates.reduce((s, c) =>
            s + moment(c.date_returned).diff(moment(c.date_sent_for_service), 'days'), 0);
        return (total / withDates.length).toFixed(1);
    }, [costs]);

    // Filter
    const filtered = costs.filter(c => {
        const veh = vehMap[String(c.vehicle_id)] || {};
        const q   = search.toLowerCase();
        const matchSearch = !q ||
            (veh.reg            || '').toLowerCase().includes(q) ||
            (veh.type           || '').toLowerCase().includes(q) ||
            (c.cost_type        || '').toLowerCase().includes(q) ||
            (c.service_provider || '').toLowerCase().includes(q) ||
            (c.job_card_no      || '').toLowerCase().includes(q);
        const matchType = !filterType || c.cost_type === filterType;
        const matchVeh  = !filterVeh  || String(c.vehicle_id) === filterVeh;
        return matchSearch && matchType && matchVeh;
    });

    const vehicleLabel = (id) => {
        const v = vehMap[String(id)];
        return v ? `${v.reg} — ${v.type}` : `Vehicle #${id}`;
    };

    return (
        <div className="page-content">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>Maintenance Log</h4>
                    <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                        Record all vehicle servicing, repairs, and costs · Petrol: 10,000 km · Diesel: 15,000 km intervals
                    </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                    <Button icon={<DashboardOutlined />}
                        type={showDash?'primary':'default'} ghost={showDash}
                        onClick={() => setShowDash(p=>!p)}>
                        {showDash ? 'Hide Dashboard' : 'Show Dashboard'}
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}
                        style={{ background: '#d46b08', borderColor: '#d46b08' }}>
                        Log Maintenance
                    </Button>
                </div>
            </div>

            {/* KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                    { label: 'Total Records',       value: costs.length,                       color: '#595959' },
                    { label: 'Vehicles Serviced',   value: uniqueVehs,                         color: '#1a5b9a' },
                    { label: 'Total Cost to Date',  value: `E ${totalCost.toLocaleString()}`,  color: '#d46b08' },
                    { label: 'Cost This Month',     value: `E ${monthCost.toLocaleString()}`,  color: '#389e0d' },
                    { label: 'Avg Turnaround',      value: avgTurnaround ? `${avgTurnaround} days` : '—', color: '#722ed1' },
                ].map(({ label, value, color }) => (
                    <div key={label} style={{
                        background: '#fff', border: '1px solid #e8e8e8',
                        borderRadius: 8, padding: '14px 16px', borderTop: `3px solid ${color}`,
                    }}>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                    </div>
                ))}
            </div>

            {/* ── Mini Dashboard ── */}
            {showDash && costs.length > 0 && (
                <MaintenanceDashboard costs={costs} vehicles={vehicles} />
            )}

            {/* ── Service Due Alert Panel ── */}
            {serviceDueVehicles.length > 0 && (
                <div style={{
                    background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8,
                    padding: '14px 16px', marginBottom: 20,
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <div style={{ fontWeight: 700, color: '#d48806', fontSize: 13 }}>
                            ⚠ {serviceDueVehicles.filter(v => v.service_due_flag === 'overdue').length} overdue
                            · {serviceDueVehicles.filter(v => v.service_due_flag === 'due_soon').length} due soon
                        </div>
                        <Button size="small" type="link"
                            onClick={() => setShowDueOnly(p => !p)}>
                            {showDueOnly ? 'Show all records' : 'Show due vehicles only'}
                        </Button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {serviceDueVehicles.map(v => (
                            <div key={v.id} style={{
                                background: '#fff', border: `1px solid ${v.service_due_flag === 'overdue' ? '#ffa39e' : '#ffe58f'}`,
                                borderRadius: 6, padding: '6px 10px', display: 'flex',
                                alignItems: 'center', gap: 8, fontSize: 12,
                            }}>
                                <span style={{ fontWeight: 700, color: '#1a5b9a' }}>{v.registration_no}</span>
                                <ServiceDueBadge flag={v.service_due_flag} />
                                {v.kmLeft !== null && (
                                    <span style={{
                                        color: v.kmLeft < 0 ? '#cf1322' : '#d48806',
                                        fontWeight: 600,
                                    }}>
                                        {v.kmLeft < 0
                                            ? `${Math.abs(v.kmLeft).toLocaleString()} km over`
                                            : `${v.kmLeft.toLocaleString()} km left`}
                                    </span>
                                )}
                                <span style={{ color: '#aaa' }}>{v.fuel_type || ''}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters */}
            <div style={{
                display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap',
                background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 6, padding: '12px 14px',
            }}>
                <Input prefix={<SearchOutlined style={{ color: '#ccc' }} />}
                    placeholder="Search vehicle, type, provider, job card…"
                    value={search} onChange={e => setSearch(e.target.value)}
                    allowClear style={{ width: 280 }} />
                <Select placeholder="Filter by work type" value={filterType || undefined}
                    onChange={setFilterType} allowClear style={{ width: 200 }}>
                    {COST_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                </Select>
                <Select showSearch placeholder="Filter by vehicle" value={filterVeh || undefined}
                    onChange={setFilterVeh} allowClear style={{ width: 220 }}
                    optionFilterProp="children">
                    {vehicles.map(v => (
                        <Option key={String(v.id)} value={String(v.id)}>
                            {v.registration_no} — {v.vehicle_type}
                        </Option>
                    ))}
                </Select>
                <div style={{ marginLeft: 'auto', fontSize: 12, color: '#888', alignSelf: 'center' }}>
                    {filtered.length} of {costs.length} records
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading maintenance records…</div>
            ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#aaa', border: '2px dashed #e8e8e8', borderRadius: 8 }}>
                    <ToolOutlined style={{ fontSize: 40, marginBottom: 12, display: 'block' }} />
                    {costs.length === 0
                        ? 'No maintenance records yet. Click "Log Maintenance" to add one.'
                        : 'No records match your filters.'}
                </div>
            ) : (
                <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: '#1a3a5c', color: '#fff' }}>
                                {['Service Date','Vehicle','Facility','Work Type','Provider',
                                  'Odometer','Next Service','Date Out','Date Back','Turnaround','Amount (E)'].map(h => (
                                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c, idx) => {
                                const veh = vehMap[String(c.vehicle_id)] || {};
                                const ta  = (c.date_sent_for_service && c.date_returned
                                    && c.date_sent_for_service !== '0000-00-00'
                                    && c.date_returned !== '0000-00-00')
                                    ? moment(c.date_returned).diff(moment(c.date_sent_for_service), 'days')
                                    : null;
                                return (
                                    <tr key={c.id}
                                        style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9f9', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                                        onClick={() => setDetailRec(c)}
                                    >
                                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                            {fmtD(c.cost_date)}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <div style={{ fontWeight: 700, color: '#1a5b9a' }}>{veh.reg || `#${c.vehicle_id}`}</div>
                                            <div style={{ fontSize: 11, color: '#aaa' }}>{veh.type || ''}</div>
                                            {veh.service_due_flag && (
                                                <ServiceDueBadge flag={veh.service_due_flag} />
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 12px', maxWidth: 120 }}>
                                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {fmt(veh.facility)}
                                            </div>
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <Tag style={{ fontSize: 11 }}>{c.cost_type}</Tag>
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>{fmt(c.service_provider)}</td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                            {c.odometer_at_service
                                                ? `${Number(c.odometer_at_service).toLocaleString()} km` : '—'}
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#1677ff' }}>
                                            {c.next_service_km
                                                ? `${Number(c.next_service_km).toLocaleString()} km` : '—'}
                                        </td>
                                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                                            {fmtD(c.date_sent_for_service)}
                                        </td>
                                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                                            {fmtD(c.date_returned)}
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700,
                                            color: ta === null ? '#ccc' : ta > 14 ? '#cf1322' : ta > 7 ? '#d46b08' : '#389e0d' }}>
                                            {ta !== null ? `${ta}d` : '—'}
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#d46b08' }}>
                                            {c.amount ? `E ${Number(c.amount).toLocaleString()}` : '—'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#f0f5ff', borderTop: '2px solid #adc6ff' }}>
                                <td colSpan={10} style={{ padding: '8px 12px', fontWeight: 700, fontSize: 12, color: '#2f54eb' }}>
                                    TOTAL ({filtered.length} records)
                                </td>
                                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: '#d46b08' }}>
                                    E {filtered.reduce((s, c) => s + Number(c.amount || 0), 0).toLocaleString()}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {/* Modals */}
            <CostFormModal
                open={formOpen} vehicles={vehicles}
                onSaved={() => { setFormOpen(false); load(); }}
                onClose={() => setFormOpen(false)}
            />
            <CostDetail
                record={detailRec}
                vehicle={detailRec ? vehicleLabel(detailRec.vehicle_id) : ''}
                onClose={() => setDetailRec(null)}
            />
        </div>
    );
}